import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { PersonalizationRow } from '@/types'
import { realNumber } from '@/lib/personalization'
import { cn } from '@/lib/cn'

/** Longer lists start folded so the size chips and artwork stay in view. */
const OPEN_UP_TO = 12

/**
 * An item's names and numbers, grouped by size in the order of its size
 * chips, in a tight grid. Shared by the order pages and the staff and
 * partner job pages. A "no num" style placeholder prints as no number.
 */
export default function NameList({ rows, sizes, className }: { rows: PersonalizationRow[]; sizes: string[]; className?: string }) {
  const [open, setOpen] = useState(rows.length <= OPEN_UP_TO)
  if (rows.length === 0) return null

  // Sizes the matrix does not list still get a group, so no row is dropped.
  const order = [...sizes, ...rows.map((row) => row.size).filter((size) => !sizes.includes(size))]
  const groups = [...new Set(order)]
    .map((size) => ({ size, rows: rows.filter((row) => row.size === size) }))
    .filter((group) => group.rows.length > 0)
  const numbered = rows.some((row) => realNumber(row.number) !== null)

  return (
    <div className={cn('text-xs', className)}>
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 font-semibold text-primary hover:underline"
      >
        {numbered ? 'Names & numbers' : 'Names'} ({rows.length})
        <span className="font-normal text-muted">{open ? 'Hide' : 'Show'}</span>
        <ChevronDown className={cn('size-3.5 transition-transform', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="mt-1.5 space-y-2 rounded-lg bg-app p-2.5">
          {groups.map((group) => (
            <div key={group.size}>
              <p className="mb-0.5 text-2xs font-semibold uppercase tracking-wide text-muted">
                {group.size} <span className="font-normal">({group.rows.length})</span>
              </p>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-0.5 sm:grid-cols-[repeat(auto-fill,minmax(8rem,1fr))]">
                {group.rows.map((row, n) => {
                  const number = realNumber(row.number)
                  return (
                    <li key={n} className="flex min-w-0 justify-between gap-2">
                      <span className="truncate text-ink" title={row.name}>
                        {row.name}
                      </span>
                      {number !== null && <span className="shrink-0 tabular-nums text-muted">#{number}</span>}
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
